// ─── Central money formatting — display only ───
// USD is always primary; the selected customer currency is secondary.
// No pricing/FX calculation happens here — formatting only.

const CURRENCY_DECIMALS: Record<string, number> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  CNY: 2,
  JPY: 0,
  KRW: 0,
  SGD: 2,
  AED: 2,
  BRL: 2,
  RUB: 2,
  INR: 2,
  VND: 0,
  TRY: 2,
  THB: 2,
  HKD: 2,
  CAD: 2,
  AUD: 2,
  CHF: 2,
  MYR: 2,
  IDR: 0,
  PHP: 2,
  TWD: 2,
  NZD: 2,
  ZAR: 2,
};

/** Decimal places for a currency code (Backend-aligned; safe defaults when unknown). */
export function currencyDecimals(code?: string | null): number {
  if (!code) return 2;
  return CURRENCY_DECIMALS[code.toUpperCase()] ?? 2;
}

function toNumber(value: string | number): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function countDecimals(value: string): number {
  const i = value.indexOf('.');
  return i < 0 ? 0 : value.length - i - 1;
}

/** Format a number with explicit min/max fraction digits (en-US grouping). */
export function formatNumber(value: string | number, minDp: number, maxDp: number): string {
  const n = toNumber(value);
  if (n === null) return String(value);
  return n.toLocaleString('en-US', {
    minimumFractionDigits: minDp,
    maximumFractionDigits: maxDp,
  });
}

/** "USD 3,350.00" style — currency CODE prefix + currency-decimal formatting. */
export function formatMoney(value: string | number, currency: string, decimalPlaces?: number): string {
  const code = (currency || 'USD').toUpperCase();
  const dp = decimalPlaces ?? currencyDecimals(code);
  return `${code} ${formatNumber(value, dp, dp)}`;
}

/** Preserve the input string's own decimal precision (canonical unit prices like "0.670000"). */
export function formatMoneyPreservePrecision(
  value: string | number,
  currency: string,
  minDp = 0,
): string {
  const code = (currency || 'USD').toUpperCase();
  if (typeof value === 'string' && value.includes('.')) {
    const dp = Math.max(minDp, countDecimals(value));
    return `${code} ${formatNumber(value, dp, dp)}`;
  }
  return `${code} ${formatNumber(value, minDp, 6)}`;
}